import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { platformWeb, useFetchPublic } from '../services'
import { getProfileData } from 'src/reducers/profileData/services/profileData'
import { REDUX_STATUS_LOADING } from '../constants'
import { selectProfileData, setProfileData } from 'src/reducers/profileData/profileDataReducer'
import { normalizeProfileData } from 'src/reducers/profileData/normalizeProfileData'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { appInstallationId, commonHeadersWeb } from '../services'
import { postData } from '../services/profileService'
import { AppConfigContext } from './AppConfigContext'
import { ProfileContext } from './ProfileContext'
import { persistPortalAvailabilityOverridesFromAuthData } from 'src/shared/config/portalAvailabilityRules'

/**
 * ParallelDataProviders - Provides both AppConfigContext and ProfileContext
 * and starts both API calls in parallel (0ms delay instead of 1-5ms)
 * 
 * This component ensures:
 * - AppConfigContext APIs (setup, locale, theme) start immediately
 * - ProfileContext API starts immediately (same render cycle)
 * - Both contexts are available to all children
 */
export const ParallelDataProviders = ({ children }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // ===== AppConfigContext APIs - Start immediately (hooks in component body) =====
  const setupFetch = useFetchPublic('/api/external/meta', '', 'web')
  const localeFetch = useFetchPublic(
    '/api/external/locale',
    `&platformType=${platformWeb}&product=com.aumhum.web`,
    'web'
  )
  const themeFetch = useFetchPublic('/api/external/theme', '', 'web')

  // ===== ProfileContext - Redux state =====
  const profileDataState = useSelector(selectProfileData)
  const profileData = profileDataState?.data
  const loading = profileDataState?.status === REDUX_STATUS_LOADING
  const isReadOnlyRescricted = getAllRolesInBoolean().isReadOnlyRescricted

  // ===== ProfileContext - AuthToken state management =====
  const [authToken, setAuthToken] = React.useState()

  const handleChangeStorage = useCallback(() => {
    const storageAuthToken = localStorage.getItem('token')
    if (storageAuthToken !== authToken) {
      setAuthToken(storageAuthToken)
    }
  }, [authToken])

  useEffect(() => {
    handleChangeStorage() // Call this when component mounts
    window.addEventListener('storage', handleChangeStorage)
    return () => window.removeEventListener('storage', handleChangeStorage)
  }, [handleChangeStorage])

  // ===== ProfileContext - FCM device token submission (independent and non-blocking) =====
  useEffect(() => {
    ;(async () => {
      // Get FCM token from localStorage (FCMProvider stores it there)
      // This allows device registration to happen independently of FCMProvider rendering
      const fcmToken = localStorage.getItem('FCMToken')
      if (fcmToken && profileData) {
        await informUserDeviceToServer(fcmToken, profileData)
      }
    })()
  }, [profileData]) // Only depends on profileData - FCM token comes from localStorage

  // ===== ProfileContext - Fetch profile data when authToken changes =====
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!isTokenInvalidOrAbsent(token) && navigate && dispatch && !profileData) {
      dispatch(getProfileData({ navigate, _forceRefresh: false }))
    }
  }, [authToken, navigate, dispatch, profileData])

  // ===== AppConfigContext value =====
  const appConfigValue = useMemo(
    () => ({
      setup: {
        loading: setupFetch.loading,
        data: setupFetch.data,
        error: setupFetch.error,
      },
      locale: {
        loading: localeFetch.loading,
        data: localeFetch.data,
        error: localeFetch.error,
        refreshFunction: localeFetch.refreshFunction,
      },
      theme: {
        loading: themeFetch.loading,
        data: themeFetch.data,
        error: themeFetch.error,
      },
    }),
    [
      setupFetch.loading,
      setupFetch.data,
      setupFetch.error,
      localeFetch.loading,
      localeFetch.data,
      localeFetch.error,
      localeFetch.refreshFunction,
      themeFetch.loading,
      themeFetch.data,
      themeFetch.error,
    ]
  )

  // ===== ProfileContext - Helper functions =====
  const fetchProfileData = useCallback(async ({ _forceRefresh = false } = {}) => {
    dispatch(getProfileData({ navigate, _forceRefresh }))
  }, [dispatch, navigate])

  const handleSetProfileData = useCallback((data) => {
    const normalizedProfileData = normalizeProfileData(data)

    // Update Redux store with new profile data
    dispatch(setProfileData(normalizedProfileData))

    // Update authorities in localStorage (critical for role-based access control)
    if (normalizedProfileData?.authorities) {
      localStorage.setItem('authorities', JSON.stringify(normalizedProfileData.authorities))
    }
    persistPortalAvailabilityOverridesFromAuthData(normalizedProfileData)
  }, [dispatch])

  // ===== ProfileContext value =====
  const profileValue = useMemo(
    () => ({
      profileData,
      setProfileData: handleSetProfileData,
      authToken,
      refreshProfileData: fetchProfileData,
      profileDataLoading: loading,
    }),
    [profileData, authToken, loading, fetchProfileData, handleSetProfileData]
  )

  return (
    <AppConfigContext.Provider value={appConfigValue}>
      <ProfileContext.Provider value={profileValue}>
        {isReadOnlyRescricted && (
          <div
            style={{
              width: '100vw',
              background: 'red',
              color: 'white',
              position: 'fixed',
              zIndex: 9999,
            }}
          >
            You are in read only mode.
          </div>
        )}
        {children}
      </ProfileContext.Provider>
    </AppConfigContext.Provider>
  )
}

// ===== FCM device token submission helper =====
async function informUserDeviceToServer(fcmToken, profileData) {
  // If FCM token was not submitted before
  if (!localStorage.getItem('submittedFCMTokenForThisUser')) {
    const body = {
      deviceId: appInstallationId,
      userId: profileData?.userId,
      platformId: 0,
      pushRegId: fcmToken,
      imei: '',
      createTime: new Date().toISOString(),
      modifiedTime: new Date().toISOString(),
      appPackage: commonHeadersWeb['ef-app-package'],
    }
    const resp = await postData('/api/secure/user/device', body)
    if (resp?.status === 200) {
      localStorage.setItem('submittedFCMTokenForThisUser', true)
    }
  }
}
